// Enabler E8-G (ADO-3109), DA-10 (AC11): a staff user picks the store account it orders for at a
// depot. A cart is always an account's, so a staff user has no cart at a depot until it has picked
// one. This picker is INTERIM: it is neither SL1.0 journey (a BWG sales rep picks a store first,
// a supplier rep a depot first) and has no design. It ships with E8-G: ADO-3110, which replaces it,
// was set aside on 2026-10-20 (story Decisions Log 49).
//
// The body of the dialog the choice screen opens from a staff depot tile: a search box and the
// accounts `GET /api/v1/ordering/accounts?depotId=&q=` finds there (at most 50; never inlined,
// since one depot can hold thousands). The search runs when asked (the Search button or Enter),
// not per keystroke. Until membership adds a name read, the server matches the code only, so
// the box is labelled "Customer code". "Nothing found" (`[]`) and "the search failed" are told
// apart. Picking an account hands it back; the page then takes the cart up for it.

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Banner, Button, FormField } from '../../design-system';
import {
  searchStaffAccounts,
  type OrderingEntry,
  type OrderingStaffAccount,
} from '../../api/ordering-choices.client';
import styles from './StaffAccountPicker.module.css';

type SearchState =
  | { readonly status: 'idle' }
  | { readonly status: 'searching'; readonly query: string }
  | { readonly status: 'failed'; readonly query: string }
  | {
      readonly status: 'found';
      readonly query: string;
      readonly accounts: readonly OrderingStaffAccount[];
    };

export interface StaffAccountPickerProps {
  /** The staff entry (depot) the account is picked at. */
  readonly entry: OrderingEntry;
  readonly onPick: (account: OrderingStaffAccount) => void;
}

/** "Spar Bunclody · 2100001", or the number alone when the name is blank. */
function staffAccountLine(account: OrderingStaffAccount): string {
  return account.accountName.trim() === ''
    ? account.customerAccountCode
    : `${account.accountName} · ${account.customerAccountCode}`;
}

export function StaffAccountPicker({ entry, onPick }: StaffAccountPickerProps) {
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState<SearchState>({ status: 'idle' });
  // The latest search only: an earlier reply that lands late is dropped.
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      generation.current += 1;
      controller.current?.abort();
    },
    [],
  );

  const run = (event?: FormEvent) => {
    event?.preventDefault();
    const asked = query.trim();
    controller.current?.abort();
    const current = ++generation.current;
    if (asked === '') {
      setSearch({ status: 'idle' });
      return;
    }
    const abort = new AbortController();
    controller.current = abort;
    setSearch({ status: 'searching', query: asked });
    searchStaffAccounts(entry.depotId, asked, { signal: abort.signal }).then(
      (accounts) => {
        if (current === generation.current) setSearch({ status: 'found', query: asked, accounts });
      },
      () => {
        if (current === generation.current) setSearch({ status: 'failed', query: asked });
      },
    );
  };

  return (
    <div className={styles.picker}>
      <form className={styles.form} role="search" onSubmit={run}>
        <FormField
          label="Customer code"
          name="customerCode"
          autoComplete="off"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
        <Button type="submit" variant="secondary" disabled={search.status === 'searching'}>
          Search
        </Button>
      </form>
      <div aria-live="polite">
        {search.status === 'searching' && (
          <p role="status" className={styles.status}>
            Searching…
          </p>
        )}
        {search.status === 'failed' && (
          <Banner type="error">
            We couldn&rsquo;t search the store accounts. Please try again.
          </Banner>
        )}
        {search.status === 'found' && search.accounts.length === 0 && (
          <p className={styles.status}>No store accounts found for &ldquo;{search.query}&rdquo;.</p>
        )}
      </div>
      {search.status === 'found' && search.accounts.length > 0 && (
        <ul className={styles.results} aria-label={`Store accounts at ${entry.label}`}>
          {search.accounts.map((account) => (
            <li key={`${account.depotCode}|${account.customerAccountCode}`}>
              <button type="button" className={styles.result} onClick={() => onPick(account)}>
                {staffAccountLine(account)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
